import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FeedbackProvider } from './components/Feedback/FeedbackProvider';
import { ApiRequestError } from './lib/api';
import { iniciarFaviconAdaptativo } from './lib/favicon';
import App from './App';
import './styles/tokens.css';
import './styles/global.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Dado financeiro que outra pessoa pode ter mudado: revalidar ao voltar
      // para a aba e barato e evita decidir sobre numero velho.
      refetchOnWindowFocus: true,
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Sessao expirada ou erro de permissao nao melhoram com nova tentativa.
        if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

// Mantém o ícone da aba coerente com o tema do navegador, inclusive quando o
// usuário troca de tema com a aba já aberta.
iniciarFaviconAdaptativo();

const container = document.getElementById('root');
if (!container) throw new Error('Elemento #root nao encontrado no index.html.');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <FeedbackProvider>
          <App />
        </FeedbackProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);

/**
 * Retira o loader de partida escrito no index.html.
 *
 * A remoção acontece aqui, e não por script inline no HTML, porque a CSP do
 * servidor não permite script inline — e essa restrição vale a pena manter.
 *
 * O `requestAnimationFrame` duplo espera o React pintar o primeiro quadro: sem
 * ele, o loader sai antes de haver o que mostrar e a tela pisca em branco no
 * intervalo.
 */
const boot = document.getElementById('boot');
if (boot) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      boot.dataset.saindo = 'true';
      boot.addEventListener('transitionend', () => {
        boot.remove();
      }, { once: true });
      // Rede de segurança: se a transição não disparar (aba em segundo plano,
      // movimento reduzido), o elemento sai mesmo assim.
      setTimeout(() => {
        boot.remove();
      }, 600);
    });
  });
}
